import { Check, ChevronDown, ChevronRight, Download, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../hooks/useAuth';
import { bilingualLinesJoined } from '../../i18n/script';
import { useSiteLanguage } from '../../i18n/useSiteLanguage';
import {
  settlementApi,
  SettlementApiError,
  type ActivityWeek,
  type SettlementWeek,
} from '../../services/settlementApi';

const DEFAULT_TIMEZONE = 'America/Los_Angeles';

function weekLabel(weekStart: string, weekEnd: string): string {
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' };
  const a = new Date(`${weekStart}T00:00:00Z`).toLocaleDateString('en-US', opts);
  const b = new Date(`${weekEnd}T00:00:00Z`).toLocaleDateString('en-US', {
    ...opts,
    year: 'numeric',
  });
  return `${a} - ${b}`;
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    timeZone: DEFAULT_TIMEZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function errMessage(err: unknown, fallback: string): string {
  return err instanceof SettlementApiError ? err.message : fallback;
}

/** Title in the viewer's language order (PRD §5.2). */
function rowTitleLines(row: ActivityWeek, lang: string): [string, string] {
  return bilingualLinesJoined(
    [[row.activityTitleCN, row.activityTitleEN]],
    lang,
    row.activityTitle
  );
}

export default function AdminSettlements() {
  const navigate = useNavigate();
  const siteLanguage = useSiteLanguage();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [weeks, setWeeks] = useState<SettlementWeek[]>([]);
  const [olderUnsettled, setOlderUnsettled] = useState<SettlementWeek[]>([]);
  const [windowStart, setWindowStart] = useState<string | null>(null);
  const [hasOlder, setHasOlder] = useState(false);
  const [unsettledOnly, setUnsettledOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyKeys, setBusyKeys] = useState<Set<string>>(() => new Set());
  const [marking, setMarking] = useState<ActivityWeek | null>(null);
  // Weeks the user explicitly expanded or collapsed; everything else follows
  // the default (fully settled weeks start collapsed).
  const [expandOverrides, setExpandOverrides] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  // Only the latest load may write state (filter toggles can race a slow response).
  const reqRef = useRef(0);
  // Latest filter for callbacks that outlive the render they were created in.
  const unsettledOnlyRef = useRef(unsettledOnly);
  unsettledOnlyRef.current = unsettledOnly;
  const windowStartRef = useRef<string | null>(null);
  windowStartRef.current = windowStart;
  const load = useCallback(async () => {
    const reqId = ++reqRef.current;
    setLoading(true);
    // A full load takes ownership of the request generation; a superseded
    // pagination request must not keep row actions locked.
    setLoadingOlder(false);
    setError(null);
    try {
      const data = await settlementApi.list({ unsettledOnly });
      if (reqId !== reqRef.current) return;
      setWeeks(data.weeks);
      setOlderUnsettled(data.olderUnsettled);
      setWindowStart(data.windowStart);
      setHasOlder(data.hasOlder);
    } catch (err) {
      if (reqId !== reqRef.current) return;
      setError(errMessage(err, 'Failed to load settlements'));
    } finally {
      if (reqId === reqRef.current) setLoading(false);
    }
  }, [unsettledOnly]);

  useEffect(() => {
    if (isAuthenticated) load();
  }, [isAuthenticated, load]);

  const showOlder = async () => {
    if (!windowStart) return;
    const reqId = ++reqRef.current;
    setLoadingOlder(true);
    setError(null);
    try {
      const data = await settlementApi.list({ before: windowStart, unsettledOnly });
      if (reqId !== reqRef.current) return;
      // Weeks that moved into the window leave the older list; the window's copy
      // wins because it includes the settled rows too.
      setWeeks((prev) => [...prev, ...data.weeks]);
      setOlderUnsettled((prev) => prev.filter((w) => w.weekStart < data.windowStart));
      setWindowStart(data.windowStart);
      setHasOlder(data.hasOlder);
    } catch (err) {
      if (reqId !== reqRef.current) return;
      setError(errMessage(err, 'Failed to load older weeks'));
    } finally {
      setLoadingOlder(false);
    }
  };

  // Reloading the whole list after a mark/undo is the simplest way to keep the
  // week counters and the older-unsettled section consistent, and the window
  // is preserved by re-fetching back to the current windowStart.
  const refreshAll = useCallback(async () => {
    const reqId = ++reqRef.current;
    const unsettledOnly = unsettledOnlyRef.current;
    const windowStart = windowStartRef.current;
    try {
      const first = await settlementApi.list({ unsettledOnly });
      let all = first.weeks;
      let cursor = first.windowStart;
      let more = first.hasOlder;
      while (more && windowStart && cursor > windowStart) {
        const page = await settlementApi.list({ before: cursor, unsettledOnly });
        all = [...all, ...page.weeks];
        cursor = page.windowStart;
        more = page.hasOlder;
      }
      if (reqId !== reqRef.current) return;
      const inWindow = new Set(all.map((w) => w.weekStart));
      setWeeks(all);
      setOlderUnsettled(first.olderUnsettled.filter((w) => !inWindow.has(w.weekStart)));
      setWindowStart(cursor);
      setHasOlder(more);
    } catch (err) {
      // A superseded refresh must not report over the newer request.
      if (reqId !== reqRef.current) return;
      throw err;
    } finally {
      // This refresh may have superseded an in-flight filter load that set
      // loading=true; the loser will not clear it, so the winner does.
      if (reqId === reqRef.current) setLoading(false);
    }
  }, []);

  const run = async (row: ActivityWeek, fn: () => Promise<unknown>) => {
    const key = `${row.activityId}|${row.weekStart}`;
    setBusyKeys((prev) => new Set(prev).add(key));
    setError(null);
    try {
      await fn();
      await refreshAll();
    } catch (err) {
      setError(errMessage(err, 'Action failed'));
      if (err instanceof SettlementApiError && err.status === 409) {
        // Someone else changed this row; reload so the UI shows their state.
        // The original 409 message stays on screen even if the reload fails.
        try {
          await refreshAll();
        } catch {
          // keep the 409 message
        }
      }
    } finally {
      setBusyKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const onExport = async (row: ActivityWeek) => {
    setError(null);
    try {
      await settlementApi.exportCsv(row);
    } catch (err) {
      setError(errMessage(err, 'Failed to export'));
    }
  };

  const onUndo = (row: ActivityWeek) => {
    if (!window.confirm('Mark this week as unsettled?')) return;
    return run(row, () => settlementApi.undo(row.activityId, row.weekStart));
  };

  const onConfirmMark = (note: string) => {
    const row = marking;
    if (!row || loadingOlder) return;
    setMarking(null);
    return run(row, () => settlementApi.mark(row.activityId, row.weekStart, note || undefined));
  };

  const renderWeek = (week: SettlementWeek) => {
    const unsettledCount = week.totalCount - week.settledCount;
    const fullySettled = unsettledCount === 0;
    const expanded = expandOverrides[week.weekStart] ?? !fullySettled;
    return (
      <div key={week.weekStart}>
        <button
          type="button"
          onClick={() => setExpandOverrides((prev) => ({ ...prev, [week.weekStart]: !expanded }))}
          className="flex min-h-12 w-full items-center justify-between gap-4 px-4 py-3 text-left hover:bg-gray-50/60 sm:px-5"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            {expanded ? (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
            {weekLabel(week.weekStart, week.weekEnd)}
          </span>
          {fullySettled ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
              <Check className="w-3.5 h-3.5 text-primary" />
              Settled
            </span>
          ) : (
            <span className="text-xs font-medium text-yellow-700">{unsettledCount} to settle</span>
          )}
        </button>
        {expanded && (
          <ul className="divide-y divide-gray-100">
            {week.activityWeeks.map((row) => {
              const key = `${row.activityId}|${row.weekStart}`;
              // Pagination and row mutations share one request generation, so
              // they are mutually exclusive in the UI rather than racing.
              const busy = busyKeys.has(key) || loadingOlder;
              const meta =
                row.settled && row.settlement?.settledAt
                  ? [
                      row.settlement.settledByName || 'Admin',
                      fmtDateTime(row.settlement.settledAt),
                      row.settlement.note,
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : row.settlement?.reminderSentAt
                    ? `Reminded ${fmtDateTime(row.settlement.reminderSentAt)}`
                    : null;
              return (
                <li
                  key={key}
                  className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-3 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_7rem_10rem_13.5rem] sm:items-center sm:px-5"
                >
                  <div className="min-w-0">
                    <Link
                      to={`/admin/activities/${row.activityId}/edit`}
                      className="text-sm font-medium text-gray-900 hover:text-primary"
                    >
                      {rowTitleLines(row, siteLanguage)[0]}
                    </Link>
                    {rowTitleLines(row, siteLanguage)[1] && (
                      <p className="text-xs text-gray-500">{rowTitleLines(row, siteLanguage)[1]}</p>
                    )}
                    <p className="mt-0.5 text-xs text-gray-500">
                      <span className="block sm:inline">{row.hostName}</span>
                      <span className="hidden sm:inline"> · </span>
                      <span className="block sm:inline">
                        {row.sessionCount} {row.sessionCount === 1 ? 'session' : 'sessions'} ·{' '}
                        {row.bookingCount} {row.bookingCount === 1 ? 'booking' : 'bookings'}
                      </span>
                    </p>
                  </div>
                  <span className="justify-self-end text-sm font-semibold tabular-nums text-gray-900">
                    {money(row.amount)}
                  </span>
                  <span className="text-xs">
                    {row.settled ? (
                      <span className="flex items-center gap-1.5 text-primary">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                        Settled
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 text-yellow-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-yellow-500" />
                        Unsettled
                      </span>
                    )}
                    {meta && (
                      <span
                        title={meta}
                        className="mt-0.5 block max-w-[12rem] truncate text-[11px] leading-4 text-gray-400"
                      >
                        {meta}
                      </span>
                    )}
                  </span>
                  <span className="col-span-2 flex justify-end gap-1 sm:col-span-1">
                    <button
                      onClick={() => onExport(row)}
                      className="inline-flex h-8 w-20 items-center justify-center gap-1 rounded-md text-xs text-gray-600 hover:bg-gray-100"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Export
                    </button>
                    {row.settled ? (
                      <button
                        disabled={busy}
                        onClick={() => onUndo(row)}
                        className="inline-flex h-8 w-[8.25rem] items-center justify-center gap-1 rounded-md text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        Undo
                      </button>
                    ) : (
                      <button
                        disabled={busy}
                        onClick={() => setMarking(row)}
                        className="inline-flex h-8 w-[8.25rem] items-center justify-center gap-1 rounded-md bg-primary text-xs font-medium text-white hover:bg-primary/90 disabled:opacity-40"
                      >
                        <Check className="w-3.5 h-3.5" />
                        Mark settled
                      </button>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  return (
    <AdminLayout
      title="Settlements"
      headerRight={
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={unsettledOnly}
            onChange={(e) => setUnsettledOnly(e.target.checked)}
            className="rounded border-gray-300"
          />
          Unsettled only
        </label>
      }
    >
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-gray-600">Loading...</p>
      ) : (
        <div className="space-y-4">
          {weeks.length === 0 && olderUnsettled.length === 0 && (
            <p className="text-gray-600">
              {unsettledOnly ? 'Nothing left to settle.' : 'No activity weeks yet.'}
            </p>
          )}
          {weeks.length > 0 && (
            <div className="divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200 bg-white">
              {weeks.map(renderWeek)}
            </div>
          )}
          {hasOlder && (
            <button
              onClick={showOlder}
              disabled={loadingOlder || busyKeys.size > 0 || marking !== null}
              className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
            >
              {loadingOlder ? 'Loading...' : 'Show older'}
            </button>
          )}
          {olderUnsettled.length > 0 && (
            <div className="pt-4">
              <h2 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                Older unsettled
              </h2>
              <div className="divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200 bg-white">
                {olderUnsettled.map(renderWeek)}
              </div>
            </div>
          )}
        </div>
      )}

      {marking && (
        <MarkSettledModal
          row={marking}
          onCancel={() => setMarking(null)}
          onConfirm={onConfirmMark}
        />
      )}
    </AdminLayout>
  );
}

function MarkSettledModal({
  row,
  onCancel,
  onConfirm,
}: {
  row: ActivityWeek;
  onCancel: () => void;
  onConfirm: (note: string) => void;
}) {
  const siteLanguage = useSiteLanguage();
  const [note, setNote] = useState('');
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Mark settled</h2>
        <p className="text-sm text-gray-600 mb-4">
          {rowTitleLines(row, siteLanguage)[0]}, {weekLabel(row.weekStart, row.weekEnd)}
        </p>
        <label className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</label>
        <input
          type="text"
          maxLength={200}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Zelle $120 on Aug 25"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <div className="mt-6 flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(note.trim())}
            className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
